/**
 * Trade Reorder Mock
 *
 * Shows how the same set of trades produces different equity
 * curves depending on the order they arrive. Three paths from
 * the same trades — losers first, winners first, and the
 * original backtest order.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const data = [
  { trade: 0, losersFirst: 100, backtestOrder: 100, winnersFirst: 100 },
  { trade: 1, losersFirst: 96, backtestOrder: 103, winnersFirst: 105 },
  { trade: 2, losersFirst: 91, backtestOrder: 101, winnersFirst: 111 },
  { trade: 3, losersFirst: 86, backtestOrder: 105, winnersFirst: 117 },
  { trade: 4, losersFirst: 83, backtestOrder: 102, winnersFirst: 122 },
  { trade: 5, losersFirst: 80, backtestOrder: 107, winnersFirst: 126 },
  { trade: 6, losersFirst: 84, backtestOrder: 104, winnersFirst: 124 },
  { trade: 7, losersFirst: 89, backtestOrder: 109, winnersFirst: 121 },
  { trade: 8, losersFirst: 95, backtestOrder: 106, winnersFirst: 118 },
  { trade: 9, losersFirst: 101, backtestOrder: 112, winnersFirst: 116 },
  { trade: 10, losersFirst: 108, backtestOrder: 115, winnersFirst: 117 },
  { trade: 11, losersFirst: 114, backtestOrder: 117, winnersFirst: 118 },
  { trade: 12, losersFirst: 118, backtestOrder: 118, winnersFirst: 118 },
];

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

export default function TradeReorderMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Same Trades, Different Order
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <ResponsiveContainer width="100%" height={150}>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 16, left: 4 }}>
            <CartesianGrid stroke="#334155" strokeWidth={0.5} vertical={false} />
            <XAxis
              dataKey="trade"
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              label={{ value: 'Trade Number', position: 'insideBottom', offset: -8, fill: '#64748b', fontSize: 10 }}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `${v}%`}
              domain={[75, 130]}
            />
            <Tooltip
              contentStyle={tipStyle}
              labelFormatter={(v) => `Trade ${v}`}
              formatter={(v: number, name: string) => [`${v}%`, name]}
              cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <Line type="monotone" dataKey="losersFirst" name="Losers first" stroke="#ef4444" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="backtestOrder" name="Backtest order" stroke="#3b82f6" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="winnersFirst" name="Winners first" stroke="#22c55e" strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.25rem' }}>
          {[
            { label: 'Losers first', color: '#ef4444' },
            { label: 'Backtest order', color: '#3b82f6' },
            { label: 'Winners first', color: '#22c55e' },
          ].map((p) => (
            <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <div style={{ width: 8, height: 3, backgroundColor: p.color, borderRadius: 1 }} />
              <span style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>{p.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="platform-mock-caption">
        All three paths use the same trades and end at the same value. Only the order differs.
      </div>
    </div>
  );
}
